import '@/styles/app-states.css';
import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';

export const ScoreGauge = ({ score, size = 120, strokeWidth = 6, label = '' }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;

  const getColor = (s) => {
    if (s >= 80) return '#16A34A';
    if (s >= 60) return 'var(--severity-major-text)';
    if (s >= 40) return '#EA580C';
    return 'var(--danger)';
  };

  return (
    <div className="relative inline-flex items-center justify-center" data-testid={`score-gauge-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#F1F5F9" strokeWidth={strokeWidth} />
        <motion.circle
          cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={getColor(score)} strokeWidth={strokeWidth} strokeLinecap="round"
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 1.5, ease: 'easeOut' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <motion.span className="font-sans font-bold text-[var(--ink-1)]" style={{ fontSize: size * 0.26 }} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}>
          {score}
        </motion.span>
        {label && <span className="text-[9px] text-slate-400 mt-0.5 font-medium">{label}</span>}
      </div>
    </div>
  );
};

export const FadeInSection = ({ children, className = '', delay = 0, ...rest }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-40px' });

  /* `...rest` forwards data-* attributes to the rendered element. Without it
     the component silently SWALLOWED them: the section-parity markers added
     in redesign Phase 4 are `data-section` attributes on these wrappers, and
     a dropped attribute would have left the guard reading source that does
     not match what ships. Props the component itself consumes are
     destructured above, so nothing is forwarded twice. */
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 24 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay, ease: 'easeOut' }} className={className} {...rest}>
      {children}
    </motion.div>
  );
};

export const StepIndicator = ({ currentStep, totalSteps, labels = [] }) => (
  <div className="flex items-center gap-2 mb-10" data-testid="step-indicator">
    {Array.from({ length: totalSteps }).map((_, i) => (
      <div key={i} className="flex items-center gap-2">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-300 ${
          i < currentStep ? 'bg-[var(--ink-1)] text-white' :
          i === currentStep ? 'bg-[var(--ink-1)] text-white ring-4 ring-[var(--ink-1)]/10' :
          'bg-slate-100 text-slate-400'
        }`}>{i + 1}</div>
        {labels[i] && <span className={`text-sm hidden sm:inline font-medium ${i <= currentStep ? 'text-[var(--ink-1)]' : 'text-slate-300'}`}>{labels[i]}</span>}
        {i < totalSteps - 1 && <div className={`w-8 md:w-12 h-px ${i < currentStep ? 'bg-[var(--ink-1)]' : 'bg-slate-200'}`} />}
      </div>
    ))}
  </div>
);

/**
 * Severity badge. Renders twice on every audit report, so this is a customer
 * surface, not an internal one.
 *
 * IT WAS RAW TAILWIND AND TWO OF ITS THREE LEVELS FAILED AA. Measured at the
 * 11px/600 it actually renders:
 *
 *   critical  #DC2626 on #FEF2F2   4.41:1   fail
 *   major     #D97706 on #FFFBEB   3.07:1   fail
 *   minor     #64748B on #F8FAFC   4.55:1   pass
 *
 * It never made the token migration, so nobody had looked. The token chips it
 * should have been using measure 5.98 / 5.55 / 6.84.
 *
 * It now renders the shared .uxa-sev chip, which also brings the three-step
 * meter with it — severity has to carry a word and a meter as well as a colour,
 * and this component carried colour and a word only.
 */
/**
 * The ONE severity chip. Fill, ink, three-step meter and the word.
 *
 * `label` overrides the word for the STATUS use — the failure banner says
 * "Audit stopped" in the major tone rather than the word "major". It exists so
 * that use does not fork the component: there were three hand-rolled copies of
 * this markup before phase G (here, the locked finding, the failure banner) and
 * each copy is another chance to drop the meter, which is the one thing
 * severity is never allowed to lose (ruled 2026-08-17: severity NEVER depends
 * on colour alone). The Minor chip is why — its fill is #FCFBFA, the page
 * canvas, 1.00:1. Without the meter and the border it is not a chip at all.
 */
export const SeverityBadge = ({ severity, label }) => {
  const level = severity === 'critical' ? 'critical' : severity === 'major' ? 'major' : 'minor';
  const filled = level === 'critical' ? 3 : level === 'major' ? 2 : 1;
  return (
    <span className={`uxa-sev uxa-sev--${level}`} data-testid={`severity-${severity}`}>
      <span className="uxa-sev-meter" aria-hidden="true">
        {[0, 1, 2].map((i) => (
          <i key={i} className={i < filled ? undefined : 'is-empty'} />
        ))}
      </span>
      {label || severity}
    </span>
  );
};
